import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import GenericDialog from '../../common/GenericDialog';
import { BrandContext } from '../../common/BrandContext';
import {BUTTON_TYPE} from "../../common/Button";
import TableAntD from "../../common/TableAntD";
import axios from "../../common/axios";
import "./teacherTests.css";
import Card, {CARD} from "../../common/Card";

const TestRecipientDialog = ({ open, onClose, testAssignmentId = null, testAssignmentName, classId }) => {


    const brandConfig = useContext(BrandContext),
        [className, setClassName] = useState(''),
        [dataSource, setDataSource] = useState([]),
        [selectedRows, setSelectedRows] = useState([]),
        [selectedRowKeys, setSelectedRowKeys] = useState([]),
        testAssignmentIdRef = useRef(testAssignmentId),
        studentIdsRef = useRef(selectedRowKeys),
        [reloadCount, setReloadCount] = useState(0);

    useEffect(()=>{
        studentIdsRef.current = selectedRowKeys
    },[selectedRowKeys])

    useEffect(()=>{
        testAssignmentIdRef.current = testAssignmentId;
    },[testAssignmentId])


    const resetStudentTests = ()=>{
        if(studentIdsRef.current.length > 0) {
            return axios.post('/web/ui/test/teacher/testAssignment/resetTestForStudents', studentIdsRef.current, {params: {testAssignmentId: testAssignmentIdRef.current}})
                .then((response) => {
                    if (response.data.success) {
                        setReloadCount(prevState => prevState + 1);
                        displayToastMessage(`${brandConfig.testName} has been reset`, '', 'success');
                    } else {
                        displayToastMessage('Error', 'Error resetting test', 'error');
                    }
                }).catch((error) => {
                    displayToastMessage('Error', `Error resetting ${brandConfig.testName}`, 'error');
                    console.error('Error resetting test assignment: ', error);
                })
        }else{
            return Promise.reject('No students selected for test reset.');
        }
    }


    const reassignStudentTests = ()=>{
        if(studentIdsRef.current.length > 0){
            return axios.post('/web/ui/test/teacher/testAssignment/reassignTestForStudents',studentIdsRef.current, {params:{testAssignmentId:testAssignmentIdRef.current}})
                .then( (response)=> {
                if(response.data.success) {
                    setReloadCount(prevState => prevState+1);
                    displayToastMessage(`${brandConfig.testName} has been reassigned`, '', 'success');
                }
            }).catch((error)=> {
                displayToastMessage('Error', 'Error reassigning test: '+error, 'error');
                console.error('Error reassigning test: ', error);
            });
        }else{
            return Promise.reject('No students selected for test reassignment.');
        }
    }

    const submitStudentTests = useCallback(()=>{
        if(studentIdsRef.current.length > 0){
            return axios.post('/web/ui/test/teacher/testAssignment/submitTestForStudents',studentIdsRef.current, {params: {testAssignmentId: testAssignmentIdRef.current} ,headers: { 'Content-Type': 'application/json' }})
                .then((response)=>{
                    if(response.data.success) {
                        setReloadCount(prevState => prevState + 1);
                        displayToastMessage(`${brandConfig.testName} has been submitted`, '', 'success');
                    }
                }).catch((error)=> {
                    displayToastMessage('Error', 'Error submitting test for student: '+error, 'error');
                    console.error('Error submitting test for student: ',error);
                });
        }else{
            return Promise.reject('No students selected for test submission');
        }
    },[])


    const calcResetOrReassign = (elem) => {return (elem.open || elem.expired)};

    const calcSubmit = (elem) => !['STARTED', 'ABANDONED', 'TIMED_OUT'].includes(elem.testAssignmentStatus);

    const buttons = useMemo(()=>{
        const resetReassignedDisabled = (selectedRows.length === 0 || selectedRows.some(calcResetOrReassign)),
            submitDisabled = selectedRows.length === 0 || selectedRows.some(calcSubmit);
        return [
        {
            name: 'Cancel',
            extraProps: { type: BUTTON_TYPE.TEXT }
        },
        {
            name: 'Reset',
            extraProps: { type: BUTTON_TYPE.OUTLINE },
            disabled: resetReassignedDisabled,
            callback: resetStudentTests
        },
        {
            name: 'Reassign',
            extraProps: { type: BUTTON_TYPE.OUTLINE },
            disabled: resetReassignedDisabled,
            callback: reassignStudentTests
        },
        {
            name: 'Submit',
            extraProps: { type: BUTTON_TYPE.OUTLINE },
            disabled: submitDisabled,
            callback: submitStudentTests
        }
    ]},[selectedRows]);



    const onSelectAllRows = (select, selectedRows) => {
        setSelectedRows(selectedRows);
    }

    const onRowSelect = (record, selected, selectedRows) => {
        setSelectedRows(selectedRows);
    }

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };


    const rowSelection={
        selectedRowKeys, checkStrictly: false,
        onChange: onSelectChange, onSelect: onRowSelect, onSelectAll: onSelectAllRows,
            getCheckboxProps: (record) => ({
            disabled: !((!record.open && !record.expired) ||
                ['STARTED', 'ABANDONED', 'TIMED_OUT'].includes(record.testAssignmentStatus)),
            name: record.name,
        })
    }

    const columns = [
        {key:'userId',title:'Student', dataIndex:'student'},
        {key:'testResultId',title:`${brandConfig.testName} status`, dataIndex:'test'}
    ];

    const adaptDataForComponent = (data)=>{
        const updatedData = [];
        data.testResults.forEach((row)=>{
            updatedData.push({key:row.userId, student:row.fullName, test:row.testAssignmentStatusDescription, ...row});
        });
        setDataSource(updatedData);
    }

    useEffect(()=>{
        if(open && testAssignmentId !== null)   {
            loadStudentTestAssignments();
        }
    },[open, testAssignmentId])

    useEffect(()=>{
        if(testAssignmentId !== null && reloadCount > 0){
            loadStudentTestAssignments();
            setSelectedRowKeys([]);
            setSelectedRows([]);
        }
    },[reloadCount])


    const loadStudentTestAssignments = ()=>{
        axios.post('/web/ui/test/teacher/testAssignment/students', null,
            {params: {testAssignmentId:testAssignmentId, classId:classId}})
            .then((response)=>{
                adaptDataForComponent(response.data);
                if(classId && response.data.class){
                    setClassName(response.data.class.className);
                }
            }).catch((error)=>{
            console.error(`Error retrieving students for ${brandConfig.testName} assignment: ${error}`)
        })
    }

    return (
        <GenericDialog
            fullWidth={true}
            maxWidth={'md'}
            title={`${brandConfig.testName} recipients for ${testAssignmentName}`}
            open={open}
            onClose={()=>{ if(onClose && typeof onClose === 'function'){
                onClose();
                setSelectedRows([]);
                setSelectedRowKeys([]);
                setDataSource([]);
            }}}
            buttons={buttons}
        >
            <h3 className="padding--s">{className}</h3>
            <TableAntD
                scroll={{y: 400}}
                rowSelection={rowSelection}
                columnData={columns}
                dataSource={dataSource}
                noDataMessage={`No ${brandConfig.testName} recipients`}
            />
            <div id={'testRecipientHelpBlk'} className={'margin-t--s smallGap flexRow padding--m'}>
                <Card boxRadius={CARD.RADIUS.TINY} padding={CARD.PADDING.SMALL}><h4 className={'ch-bold ch-std-font'}>Reset</h4><ul><li>Answers will be removed.</li><li>Started/Completed and non-Expired assignments can be reset.</li></ul></Card>
                <Card boxRadius={CARD.RADIUS.TINY} padding={CARD.PADDING.SMALL}><h4 className={'ch-bold ch-std-font'}> Reassign</h4><ul><li>Answers will be kept.</li><li>Started/Completed and non-Expired assignments can be reassigned.</li></ul></Card>
                <Card boxRadius={CARD.RADIUS.TINY} padding={CARD.PADDING.SMALL}><h4 className={'ch-bold ch-std-font'}> Submit</h4><ul><li>Answers will be submitted.</li><li>Started, Abandoned, or Timed out assignments can be submitted.</li></ul></Card>
            </div>
        </GenericDialog>
    );
};

export default TestRecipientDialog;
